/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ConfirmationModel, ModalModel } from '../../../models';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { LoadingService, LogService } from '../../';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';

@Injectable()
export class ModalService {
    /**
     * Allow for components to listen for modals closing
     * Note: Use case - Knowing when a modal closes to update information on the page
     */
    public openParentModalEmitter: Subject<any> = new Subject<any>();

    /**
     * Allow for components to listen for modals closing
     * Note: Use case - Knowing when a modal closes to update information on the page
     */
    public closeParentModalEmitter: Subject<any> = new Subject<any>();

    /**
     * Allow for components to listen for modals closing
     * Note: Use case - Knowing when a modal closes to update information on the page
     */
    public closeAllParentModalEmitter: Subject<any> = new Subject<any>();

    /**
     * Cancel button emitter
     */
    public cancelSubmission: Subject<boolean> = new Subject<boolean>();

    /**
     * Stored modal
     * @private
     */
    private _modals: ModalModel[] = [];

    /**
     * The confirmation screen object
     */
    private _confirmationObject: ConfirmationModel = {
        title: '',
        message: '',
        closeInternally: true,
        callBack: Function,
        options: {
            translateText: true,
            hideCancelButton: false,
            confirmTextKey: 'confirm',
            isDraggable: true,
            isOverlayBlurred: false,
            isOverlayClickable: false,
            showCloseButton: true
        }
    };

    /**
     * Close emitter
     */
    @Output() public dragActiveEmitter: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private loadingService: LoadingService,
        private logService: LogService,
        private router: Router
    ) {
        this.subscribeToRouteChanges();
    }

    set modals(modal: any) {
        const matchedIndex = this._modals.findIndex(item => item.id === modal.id);
        if (matchedIndex !== -1) {
            this._modals.splice(matchedIndex);
        }

        this._modals.push(modal);
    }

    get modals() {
        return this._modals;
    }

    set confirmationObject(confirm: ConfirmationModel) {
        this._confirmationObject = confirm;
    }

    get confirmationObject(): ConfirmationModel {
        return this._confirmationObject;
    }

    set dragActive(state: boolean) {
        this.dragActiveEmitter.next(state);
    }

    /**
     * Pass the selected modal to the parent modal.
     * @param id
     */
    public openModalInParent(id: string): void {
        this.loadingService.loadingButtonEmitter.next({ id: 'modal-confirm', state: false });
        const modal = this.modals.find((item: ModalModel) => item.id === id);
        modal.isOpen = true;
        this.openParentModalEmitter.next(modal);
    }

    /**
     * Is modal open.
     * @param id
     */
    public isModelOpen(id: string): boolean {
        const modal = this.findModal(id);
        if (modal) {
            return modal.isOpen;
        }
    }

    /**
     * Finds a modal in our array and closes it!
     * @param id
     */
    public close(id: string): void {
        const modal = this.modals.find((item: ModalModel) => item.id === id);
        modal.isOpen = false;
        this.closeParentModalEmitter.next(id);
    }

    /**
     * Close all modals
     */
    public closeAll(): void {
        for (const modal of this.modals) {
            modal.isOpen = false;
        }
        this.closeAllParentModalEmitter.next(true);
    }

    /**
     * Function is used to find a modal within our array by its ID
     * @param id
     */
    public findModal(id: string): ModalModel {
        return this.modals.find((item: ModalModel) => item.id === id);
    }

    /**
     * Confirm decision.
     * @param confirmationObject
     */
    public confirmDecision(confirmationObject: ConfirmationModel): void {
        const { title, message, closeInternally = true, callBack, options } = confirmationObject;

        if (!title.length || !message.length) {
            this.logService.error(
                'ModalService:Confirm()',
                'Unable to create confirmation box as the title or message are missing.'
            );
        } else {
            const formattedOptions = {
                confirmTextKey: this.formatOptions(options, 'confirmTextKey'),
                translateText: this.formatOptions(options, 'translateText'),
                hideCancelButton: this.formatOptions(options, 'hideCancelButton'),
                isDraggable: this.formatOptions(options, 'isDraggable'),
                isOverlayBlurred: this.formatOptions(options, 'isOverlayBlurred'),
                isOverlayClickable: this.formatOptions(options, 'isOverlayClickable'),
                showCloseButton: this.formatOptions(options, 'showCloseButton')
            };

            this.confirmationObject = {
                title: title,
                message: message,
                closeInternally,
                callBack,
                options: formattedOptions
            };

            this.openModalInParent('confirmation-box');
        }
    }

    /**
     * Format options
     * @param options
     * @param key
     * @private
     */
    private formatOptions(options: any, key: string): any {
        const defaultOptions = {
            confirmTextKey: 'confirm',
            translateText: true,
            hideCancelButton: false,
            isDraggable: true,
            isOverlayBlurred: false,
            isOverlayClickable: false,
            showCloseButton: true
        };

        return (options[key] || options[key] === false) ? options[key] : defaultOptions[key];
    }

    /**
     * Subscribe to route changes.
     * @private
     */
    private subscribeToRouteChanges(): void {
        this.router.events.subscribe({
            next: (event: any): void => {
                if (event instanceof NavigationEnd) {
                    this.closeAll();
                }
            }
        });
    }
}
